import { useMutation, useReactiveVar } from '@apollo/client'
import Cookies from 'js-cookie'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useSearchParams } from 'react-router-dom'
import validator from 'validator'
import LOGO from '../../assets/logo/lancape_no_bg.png'
import {
  Button,
  CheckboxItem,
  CheckboxType,
  Icon,
  IconType,
  TextField,
} from '../../components'
import { CREATE_USER } from '../../gql'
import { CountryCode, Currency, Session, User, UserType } from '../../gql/types'
import { ErrorCode } from '../../utils/ErrorCode'
import { PublicRoutes } from '../../utils/PublicRoutes'
import { WebLink } from '../../utils/WebLink'
import {
  AnalyticsEvent,
  getMetaCookies,
  trackEvent,
} from '../../utils/analytics'
import { AUTH_TOKEN, Locale } from '../../hooks'
import { Params, captureQueryParams } from '../../utils/params'

export type CreateUserFormData = {
  firstName: string
  lastName: string
  email: string
  phoneNumberCountry: CountryCode
  phoneNumber: string
  password: string
  countryCode: CountryCode
  currency: Currency
  timezone: string
  utcOffset: number
  isMarketingEmailsSubscribed: boolean
  hasAcceptedTerms: boolean
}

const RegisterCreator = () => {
  const [searchParams] = useSearchParams()
  captureQueryParams(searchParams)

  const locale = useReactiveVar(Locale)
  const [createUser, { loading }] = useMutation<{
    createUser: {
      user: User
      session: Session
    }
  }>(CREATE_USER)

  useEffect(() => {
    trackEvent(AnalyticsEvent.viewCreatorsRegister)
  }, [])

  const {
    register,
    handleSubmit,
    control,
    setError,
    setValue,
    formState: { errors },
    watch,
  } = useForm<CreateUserFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumberCountry: CountryCode.US,
      phoneNumber: '',
      password: '',
      countryCode: locale.countryCode,
      currency: locale.currency,
      timezone: locale.timezone,
      utcOffset: locale.utcOffset,
      isMarketingEmailsSubscribed: true,
      hasAcceptedTerms: false,
    },
  })

  const onSubmit = async (form: CreateUserFormData) => {
    const meta = getMetaCookies()

    try {
      const { data } = await createUser({
        variables: {
          input: {
            type: UserType.creator,
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            password: form.password,
            countryCode: form.countryCode,
            phoneNumberCountry: form.phoneNumberCountry,
            phoneNumber: form.phoneNumber,
            currency: form.currency,
            timezone: form.timezone,
            utcOffset: form.utcOffset,
            isMarketingEmailsSubscribed:
              form.isMarketingEmailsSubscribed &&
              form.isMarketingEmailsSubscribed === true
                ? true
                : false,
            fbp: meta.fbp,
            fbc: meta.fbc,
            utmSource: localStorage.getItem(Params.utm_source),
            utmMedium: localStorage.getItem(Params.utm_medium),
            utmCampaign: localStorage.getItem(Params.utm_campaign),
            utmTerm: localStorage.getItem(Params.utm_term),
            utmContent: localStorage.getItem(Params.utm_content),
          },
        },
      })

      trackEvent(AnalyticsEvent.creatorSignedUp)
      localStorage.setItem(AUTH_TOKEN, data.createUser.session.token)
      window.location.reload()
    } catch (error) {
      if (error.message === ErrorCode.emailAlreadyTaken) {
        setError('email', { type: 'custom', message: 'Email already taken.' })
        return
      }
    }
  }

  return (
    <div>
      <div className='min-h-full flex flex-col justify-center py-12 sm:px-6 lg:px-8'>
        <div className='sm:mx-auto sm:w-full sm:max-w-lg'>
          <img className='mx-auto h-12 w-auto' src={LOGO} alt='Workflow' />
          <h2 className='mt-6 text-center text-3xl font-extrabold text-gray-900'>
            Join as a Creator
          </h2>
          <p className='mt-2 text-center text-sm text-gray-600'>
            Are you a brand?{' '}
            <Link
              to={PublicRoutes.registerBrand}
              className='font-medium text-indigo-500 hover:text-indigo-600'
            >
              Join as a brand
            </Link>
          </p>
        </div>

        <div className='mt-8 sm:mx-auto sm:w-full sm:max-w-lg'>
          <div className='py-8 px-4 sm:px-10'>
            <form className='space-y-6' onSubmit={handleSubmit(onSubmit)}>
              <div className='space-y-6 md:grid md:grid-cols-2 md:gap-2 md:space-y-0'>
                <div className=''>
                  <TextField
                    error={errors.firstName?.message}
                    label='First name'
                    placeholder='Lisa'
                    {...register('firstName', {
                      required: `Please set a first name.`,
                      maxLength: {
                        value: 100,
                        message: `Your first name cannot have more than 100 letters.`,
                      },
                    })}
                  />
                </div>

                <div className=''>
                  <TextField
                    error={errors.lastName?.message}
                    label='Last name'
                    placeholder='Mayer'
                    {...register('lastName', {
                      required: `Please set a last name.`,
                      maxLength: {
                        value: 100,
                        message: `Your last name cannot have more than 100 letters.`,
                      },
                    })}
                  />
                </div>
              </div>

              <div>
                <TextField
                  error={errors.email?.message}
                  label='Email'
                  placeholder='you@example.com'
                  {...register('email', {
                    required: `Please set an email.`,
                    maxLength: {
                      value: 100,
                      message: `Please keep your email under 100 letters.`,
                    },
                    validate: {
                      value: (email) => {
                        if (validator.isEmail(email)) {
                          return true
                        }

                        return 'Invalid email address.'
                      },
                    },
                  })}
                />
              </div>

              <div>
                <TextField
                  error={errors.phoneNumber?.message}
                  label='Phone Number'
                  placeholder='07450 917 213'
                  // className='pl-16'
                  // leftElement={
                  //   <div className='absolute inset-y-0 left-0 flex items-center'>
                  //     <Controller
                  //       name='phoneNumberCountry'
                  //       control={control}
                  //       rules={{
                  //         required: `Please set a phone number country.`,
                  //       }}
                  //       render={({ field }) => (
                  //         <Select
                  //           options={[
                  //             CountryCode.GB,
                  //             // CountryCode.US,
                  //             // CountryCode.AU,
                  //           ]}
                  //           field={field}
                  //           className='-mt-1 w-16 rounded-r-none border-none bg-transparent'
                  //         />
                  //       )}
                  //     />
                  //   </div>
                  // }
                  {...register('phoneNumber', {
                    required: `Please set a phone number.`,
                    minLength: {
                      value: 6,
                      message: `Please set a real phone number.`,
                    },
                    maxLength: {
                      value: 20,
                      message: `Please set a real phone number.`,
                    },
                  })}
                />
              </div>

              <div>
                <label
                  htmlFor='password'
                  className='block text-sm font-medium text-gray-700'
                >
                  Password
                </label>
                <div className='relative mt-1'>
                  <input
                    type='password'
                    placeholder='******'
                    className='appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-500 focus:outline-none focus:ring-gray-800 focus:border-gray-800 sm:text-sm'
                    {...register('password', {
                      required: `Please set a password.`,
                      minLength: {
                        value: 6,
                        message: `Please set a password with minimum 6 letters.`,
                      },
                      maxLength: {
                        value: 100,
                        message: `Please keep your password under 100 letters.`,
                      },
                    })}
                  />
                  {errors.password?.message && (
                    <div className='absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none'>
                      <Icon
                        type={IconType.exclamationCircleFill}
                        className='h-5 w-5 text-red-500'
                        aria-hidden='true'
                      />
                    </div>
                  )}
                </div>
                {errors.password?.message && (
                  <p className='mt-2 text-sm text-red-600' id='email-error'>
                    {errors.password?.message}
                  </p>
                )}
              </div>

              <div className='flex items-center justify-between'>
                <div className='flex items-center'>
                  <CheckboxItem
                    type={CheckboxType.checkbox}
                    value={!watch('isMarketingEmailsSubscribed')}
                    title='Email me about product updates and helpful resources'
                    {...register('isMarketingEmailsSubscribed')}
                    selected={watch('isMarketingEmailsSubscribed')}
                    className='-m-4'
                  />
                  {/* <input
                    type='checkbox'
                    className='h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded'
                    {...register('isMarketingEmailsSubscribed')}
                  />
                  <label
                    htmlFor='remember-me'
                    className='ml-2 block text-sm text-gray-600'
                  >
                    Email me about product updates and helpful resources
                  </label> */}
                </div>
              </div>

              <div>
                <div className='flex items-start'>
                  <input
                    id='hasAcceptedTerms'
                    type='checkbox'
                    className='mt-0.5 h-4 w-4 rounded border-gray-300 text-gray-800 focus:ring-gray-500 cursor-pointer'
                    {...register('hasAcceptedTerms', {
                      required:
                        'Please accept the Terms of Service and Privacy Policy to continue.',
                    })}
                  />
                  <span className='ml-3 text-sm text-gray-600'>
                    I agree to Clip’s{' '}
                    <a
                      href={WebLink.tos}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='font-medium text-indigo-500 hover:text-indigo-600'
                    >
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a
                      href={WebLink.privacyPolicy}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='font-medium text-indigo-500 hover:text-indigo-600'
                    >
                      Privacy Policy
                    </a>
                  </span>
                </div>
                {errors.hasAcceptedTerms?.message && (
                  <p className='mt-2 text-sm text-red-600'>
                    {errors.hasAcceptedTerms?.message}
                  </p>
                )}
              </div>

              <div>
                <Button
                  title='Create account'
                  type='submit'
                  loading={loading}
                  disabled={loading}
                  width='w-full'
                />
              </div>
            </form>

            <div className='mt-6'>
              <div className='relative'>
                <div className='relative flex justify-center text-sm'>
                  <p className='px-2 text-center text-sm text-gray-600'>
                    Have an account already?{' '}
                    <Link
                      to={PublicRoutes.login}
                      className='font-medium text-indigo-500 hover:text-indigo-600'
                    >
                      Sign in
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export { RegisterCreator }
